import {deleteAdditionalComponent} from "./estimate-additional-component.service";
import {useNavigate} from "react-router-dom";
import React, {useState} from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack
} from "@mui/material";
import {enqueueSnackbar} from "notistack";
import {logError} from "../../shared/services/logger.service";
import {EstimateAdditionalComponentDto} from "../../shared/dtos/estimate-additional-component.dto";
import {EstimateTreeDto} from "../../shared/dtos/estimate-tree.dto";

export interface EstimateChipperTruckDetailComponentProps {
  chipperTruck: EstimateAdditionalComponentDto;
  chipperLocalId: number;
  estimateTrees: EstimateTreeDto[];
  treeLocalIdMap: {[key: number]: number};
  /** Ids of work items currently connected to this chipper via a dragline. */
  connectedTreeIds: number[];
  onSave: (chipperTruck: EstimateAdditionalComponentDto, selectedTreeIds: number[]) => Promise<void>;
  onDelete: () => void;
  onCancel: () => void;
  mode: 'read-only' | 'edit';
}

const formatWorkItemForDisplay = (tree: EstimateTreeDto, localId: number) => `${localId} - ${tree.workItemType}`;

const EstimateChipperTruckDetailComponent: React.FC<EstimateChipperTruckDetailComponentProps> = React.forwardRef((props, ref) => {
  const mode = props.mode ?? 'edit';
  const canEdit = mode === 'edit';

  const navigate = useNavigate();

  const orderedWorkItems = [...props.estimateTrees].sort((a, b) =>
    (props.treeLocalIdMap[a.id] ?? 0) - (props.treeLocalIdMap[b.id] ?? 0)
  );

  const [selectedTreeIds, setSelectedTreeIds] = useState<number[]>(props.connectedTreeIds);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

  const handleWorkItemsChange = (event: SelectChangeEvent<number[]>) => {
    const {
      target: {value},
    } = event;
    setSelectedTreeIds(value as number[]);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  }

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false);
    setIsDeleting(true);
    deleteAdditionalComponent(navigate, props.chipperTruck.estimateId, props.chipperTruck.id)
      .then(() => {
        props.onDelete();
      })
      .catch(e => {
        logError('Error deleting truck & chipper', {}, e);
        enqueueSnackbar(`Error deleting truck & chipper: ${e}`, {variant: 'error', autoHideDuration: 5000});
      })
      .finally(() => {
        setIsDeleting(false);
      });
  }

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
  }

  const handleSaveAndReturnToMap = async () => {
    setIsSaving(true);
    await props.onSave(props.chipperTruck, selectedTreeIds);
    setIsSaving(false);
  }

  return (
    <Card
      sx={{ maxWidth: 600, maxHeight: '90vh', minHeight: '20vh', mx: 'auto', my: 4, overflowY: 'auto' }}>
      <CardContent>
        <CardHeader title={`Truck & Chipper ${props.chipperLocalId}`}/>
        <Stack spacing={2}>
          <FormControl>
            <InputLabel id="chipper-work-items-select-label">Connect Work Items to Truck & Chipper</InputLabel>
            <Select
              labelId="chipper-work-items-select-label"
              disabled={!canEdit}
              multiple
              label="Connect Work Items to Truck & Chipper"
              value={selectedTreeIds}
              onChange={handleWorkItemsChange}
              input={<OutlinedInput label="Connect Work Items to Truck & Chipper"/>}
              renderValue={(selected) => orderedWorkItems
                .filter(tree => selected.includes(tree.id))
                .map(tree => formatWorkItemForDisplay(tree, props.treeLocalIdMap[tree.id]))
                .join(', ')}
            >
              {orderedWorkItems.map((tree) => (
                <MenuItem key={tree.id} value={tree.id}>
                  <Checkbox checked={selectedTreeIds.includes(tree.id)} />
                  <ListItemText primary={formatWorkItemForDisplay(tree, props.treeLocalIdMap[tree.id])} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button onClick={handleDeleteClick} variant="contained" color="error" style={{color: 'black'}} component="label" disabled={!canEdit || isDeleting}>
            {isDeleting ? <CircularProgress size={24}/> : 'Delete Truck & Chipper'}
          </Button>
          {canEdit
            ? <Button onClick={handleSaveAndReturnToMap} variant="outlined" component="label" disabled={isSaving}>{isSaving ? <CircularProgress size={24}/> : 'Save and Return to Map'}</Button>
            : <Button onClick={props.onCancel} variant="outlined" component="label">Return to Map</Button>
          }
        </Stack>
      </CardContent>
      <Dialog open={deleteDialogOpen} onClose={handleDeleteClose}>
        <DialogTitle>Delete Truck & Chipper?</DialogTitle>
        <DialogActions sx={{ justifyContent: 'space-around', padding: '8px' }}>
          <Button onClick={handleDeleteClose} variant="contained" color="primary" style={{color: 'black'}}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" style={{color: 'black'}} autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
});

export default EstimateChipperTruckDetailComponent;
